import { useState } from 'react';

import { IconAlertOctagonFilled, IconTrash } from '@tabler/icons-react';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  Divider,
  Dropdown,
  TextField,
} from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import ConfirmationModal from 'components/popups/ConfirmationModal';
import Constants from 'constants/index';
import { validateTextField } from 'utils/validateTextField';

import BuildQuizzesAnswers, { BuildQuizzesProps } from './BuildQuizzesAnswers';

import { TranslationKey } from '@/i18n';

interface QuestionTemplateType {
  id: string;
  templateName: TranslationKey;
  isMultipleChoiceTemplate: boolean;
}

const BuildQuizzesQuestionCard: React.FC<BuildQuizzesProps> = ({
  handlePassingGrade,
  questionIndex,
  quiz,
  setQuiz,
  setValidationMode,
  validationMode,
}) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const userLanguage = auth?.language ?? 'en';
  const questionTemplateTypesArray = Object.values(
    Constants.QUESTION_TEMPLATE_TYPES,
  );

  const [showDeleteQuestionPopup, setShowDeleteQuestionPopup] = useState(false);
  const answers = quiz.questions[questionIndex]?.answers.map(({ ...rest }) => ({
    ...rest,
  }));
  const defaultTrueFalseAnswers =
    Constants.DEFAULT_TRUE_FALSE_QUESTION.answers.map(({ ...rest }) => ({
      ...rest,
    }));
  const questionTemplateType = isEqual(answers, defaultTrueFalseAnswers)
    ? Constants.QUESTION_TEMPLATE_TYPES.TRUE_OR_FALSE
    : Constants.QUESTION_TEMPLATE_TYPES.MULTIPLE_CHOICE;
  const correctOptionSelected =
    quiz.questions[questionIndex]?.answers.some(
      option => option.correct === true,
    ) ?? false;

  const handleQuestionName = (value: string) => {
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              question: { ...question.question, [userLanguage]: value },
            }
          : question,
      ),
    }));
  };

  const handleTemplateChange = (value: QuestionTemplateType | null) => {
    if (!value || value.id === questionTemplateType.id) return;
    setValidationMode(false);
    const newTemplateAnswers =
      value.id === Constants.QUESTION_TEMPLATE_TYPES.MULTIPLE_CHOICE.id
        ? Constants.DEFAULT_MULTIPLE_CHOICE_QUESTION
        : Constants.DEFAULT_TRUE_FALSE_QUESTION;
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? { ...question, ...newTemplateAnswers }
          : question,
      ),
    }));
  };

  const deleteQuestion = () => {
    handlePassingGrade?.();
    // Note: No need to manually save to storage here - the parent component's
    // useEffect in BuildQuizzesView automatically saves quiz state to the atom
    // when it changes via setQuiz
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.filter(
        (_, index) => index !== questionIndex,
      ),
    }));
    setShowDeleteQuestionPopup(false);
  };

  return (
    <>
      <Card elevation={1} variant="outlined" className="mb-4">
        <CardContent>
          <div className="flex flex-col items-baseline justify-center gap-2 md:flex-row">
            <div className="h-12 w-full flex-2 md:w-auto">
              <Dropdown
                data-testid={t(questionTemplateType.templateName).replace(
                  /\s/g,
                  '',
                )}
                fullWidth
                getOptionId={option => option.id}
                getOptionText={option => t(option.templateName)}
                onChange={newVal => handleTemplateChange(newVal)}
                options={questionTemplateTypesArray}
                renderOption={option => t(option.templateName)}
                value={questionTemplateType}
              />
            </div>
            <div className="w-full flex-4 md:w-auto">
              <TextField
                data-testid="EnterQuestion"
                errorText={
                  !validateTextField(
                    quiz.questions[questionIndex]?.question[userLanguage] ?? '',
                  ) && validationMode
                    ? t('TrainingPlans.buildQuizzes.errorQuestion')
                    : ''
                }
                fullWidth
                label={`${t('Generic.question')} ${questionIndex + 1}`}
                maxLength={250}
                onChange={e => handleQuestionName(e.target.value)}
                placeholder={t('TrainingPlans.buildQuizzes.enterQuestion')}
                required
                value={
                  quiz.questions[questionIndex]?.question[userLanguage] ?? ''
                }
              />
            </div>
          </div>

          <div className="mt-4">
            {questionTemplateType.isMultipleChoiceTemplate
              ? t('TrainingPlans.buildQuizzes.buildQuestionMultipleChoice')
              : t('TrainingPlans.buildQuizzes.buildQuestionTrueFalse')}
            {!correctOptionSelected && validationMode && (
              <div className="text-feedback-error-base mt-2 flex w-full items-center text-xs font-semibold">
                <IconAlertOctagonFilled className="mr-2 size-4" />
                {t('TrainingPlans.buildQuizzes.errorSelectCorrectAnswer')}
              </div>
            )}
          </div>

          <BuildQuizzesAnswers
            questionIndex={questionIndex}
            questionTemplateType={questionTemplateType}
            quiz={quiz}
            setQuiz={setQuiz}
            setValidationMode={setValidationMode}
            validationMode={validationMode}
          />

          {quiz.questions.length > 1 && (
            <>
              <Divider orientation="horizontal" variant="middle" />
              <div className="my-8 mr-4 flex justify-end">
                <IconTrash
                  onClick={() => setShowDeleteQuestionPopup(true)}
                  size={24}
                  className="text-grayscale-gray6 cursor-pointer"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal
        bodyText={t('Generic.deleteUnnamedObject', {
          object: t('Generic.question').toLowerCase(),
        })}
        headerText={t('Generic.deleteHeader', {
          type: t('Generic.question'),
        })}
        isOpen={showDeleteQuestionPopup}
        onClose={() => setShowDeleteQuestionPopup(false)}
        primaryButtonColor={'primary'}
        primaryButtonHandler={deleteQuestion}
        primaryButtonText={t('Button.delete')}
        primaryButtonVariant="destructive"
        secondaryButtonHandler={() => setShowDeleteQuestionPopup(false)}
        secondaryButtonText={t('Button.cancel')}
      />
    </>
  );
};

export default BuildQuizzesQuestionCard;
